'use client';

import useSWR from 'swr';

import { useCurrentMonthScope } from '@/hooks/useCurrentMonthScope';
import { homeApi } from '@/lib/api';
import { calendarScopeQuery } from '@/lib/calendarScope';
import type { NetWorthTrendWidget } from '@/types';
import { LONG_LIVED_SWR_OPTIONS, useAuthedFetcher } from './swrConfig';

/**
 * W4 · Patrimônio · 6 meses — net-worth trend from `GET /api/home/net-worth`
 * (headline net worth, assets/debts totals, MoM delta and 6 monthly points).
 * The `home-widget-` key prefix is registered in FINANCIAL_DATA_KEY_PREFIXES,
 * so the existing mutation/sync revalidation sweeps refresh this widget too.
 *
 * The window ends on the CLIENT's calendar month (`calendarScopeQuery`), not the server's
 * UTC month; the key carries it so an open tab refetches at the local turn of the month.
 */
export function useNetWorthTrend(): {
  data: NetWorthTrendWidget | undefined;
  error: unknown;
  isLoading: boolean;
} {
  const authedFetch = useAuthedFetcher();
  const { year, month } = useCurrentMonthScope();

  const { data, error, isLoading } = useSWR<NetWorthTrendWidget>(
    ['home-widget-net-worth', year, month],
    () => authedFetch((token) => homeApi.getNetWorthTrend(token, calendarScopeQuery())),
    LONG_LIVED_SWR_OPTIONS,
  );

  return { data, error, isLoading };
}
